// /app/frontend/src/components/frugal-v2/components/reports/CashflowSection.jsx
// Cashflow timeline - i18n + flat layout (no double container)

import React, { useEffect, useState } from 'react';
import { AlertTriangle, TrendingDown, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CashflowSection = ({ token, year, month, t, lang = 'es', flat = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = year && month ? `?year=${year}&month=${month}` : '';
        const res = await fetch(`${API}/api/v2/frugal/reports/cashflow${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('failed');
        const json = await res.json();
        if (!cancel) setData(json);
      } catch {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [token, year, month]);

  const fmt = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const dateLocale = lang.startsWith('es') ? 'es-ES' : 'en-US';

  if (loading) {
    return <div className="text-center text-slate-400 text-sm py-4">{t.cfCalculating}</div>;
  }
  if (!data || !data.rows?.length) {
    return <div className="text-center text-slate-400 text-sm py-4">{t.cfEmpty}</div>;
  }

  const { anchor, rows, alerts, summary } = data;
  const today = data.period.today;

  return (
    <div className="space-y-4">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2.5">
          <div className="text-slate-400">{t.cfBalanceToday}</div>
          <div className="text-emerald-300 font-semibold text-sm">${fmt(anchor.bank_balance_today)}</div>
        </div>
        <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-2.5">
          <div className="text-slate-400">{t.cfFinalBalance}</div>
          <div className={`font-semibold text-sm ${summary.final_balance < 0 ? 'text-rose-400' : 'text-sky-300'}`}>
            ${fmt(summary.final_balance)}
          </div>
        </div>
        <div className="bg-white/5 rounded-lg p-2.5">
          <div className="text-slate-400 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-emerald-400" /> {t.cfIncomingIncome}</div>
          <div className="text-emerald-300 font-semibold">${fmt(summary.income_pending)}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-2.5">
          <div className="text-slate-400 flex items-center gap-1"><TrendingDown className="w-3 h-3 text-rose-400" /> {t.cfUpcomingExpenses}</div>
          <div className="text-rose-300 font-semibold">${fmt(summary.expense_pending)}</div>
        </div>
      </div>

      {/* Negative balance alerts */}
      {alerts && alerts.length > 0 && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3 space-y-1">
          <div className="flex items-center gap-2 text-rose-300 font-medium text-sm">
            <AlertTriangle className="w-4 h-4" />
            {t.cfNegativeRiskTitle}
          </div>
          {alerts.slice(0, 3).map((a, idx) => {
            const d = new Date(a.date + 'T00:00:00');
            return (
              <div key={idx} className="text-xs text-rose-200 ml-6">
                <strong>{d.toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' })}</strong>: ${fmt(a.balance)}
              </div>
            );
          })}
          {summary.lowest && (
            <div className="text-[11px] text-amber-200 mt-1.5 ml-6 italic">
              {t.cfLowestPoint}: ${fmt(summary.lowest.balance_after)} — {new Date(summary.lowest.date + 'T00:00:00').toLocaleDateString(dateLocale, { day: '2-digit', month: 'short' })}
            </div>
          )}
        </div>
      )}

      {/* Timeline table - mobile-optimized: narrow date, wider concept */}
      <div className="bg-white/5 rounded-lg overflow-hidden">
        <div className="flex items-center px-2 py-1.5 text-[10px] text-slate-500 uppercase tracking-wide border-b border-white/10 gap-2">
          <div className="w-12 flex-shrink-0">{t.cfDate}</div>
          <div className="flex-1 min-w-0">{t.cfConcept}</div>
          <div className="w-20 text-right flex-shrink-0">{t.cfMov}</div>
          <div className="w-20 text-right flex-shrink-0">{t.cfBalance}</div>
        </div>
        <div className="divide-y divide-white/5 max-h-96 overflow-y-auto">
          {rows.map((r, idx) => {
            const past = r.date <= today;
            const negative = r.balance_after < 0;
            const d = new Date(r.date + 'T00:00:00');
            return (
              <div
                key={idx}
                className={`flex items-center px-2 py-1.5 text-xs gap-2 ${negative ? 'bg-rose-500/10' : past ? 'opacity-70' : ''}`}
                data-testid={`cashflow-row-${idx}`}
              >
                <div className="w-12 flex-shrink-0 text-slate-400 tabular-nums">
                  {d.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit' })}
                </div>
                <div className="flex-1 min-w-0 truncate text-slate-200">
                  {r.concept}
                </div>
                <div className={`w-20 text-right flex-shrink-0 font-medium tabular-nums ${r.amount < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {r.amount > 0 ? '+' : ''}${fmt(r.amount)}
                </div>
                <div className={`w-20 text-right flex-shrink-0 font-semibold tabular-nums ${negative ? 'text-rose-300' : 'text-white'}`}>
                  ${fmt(r.balance_after)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[11px] text-slate-500 text-center italic">
        {t.cfFooter}
      </p>
    </div>
  );
};

export default CashflowSection;
